import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, RefreshCw, Search, Printer } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../api/axiosConfig';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(centavos) {
  return `₱${((centavos || 0) / 100).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-PH', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function duration(start, end) {
  if (!start) return '—';
  const ms = (end ? new Date(end) : new Date()) - new Date(start);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

const PRESET_DATES = {
  today: () => {
    const d = new Date();
    return { dateFrom: d.toISOString().split('T')[0], dateTo: d.toISOString().split('T')[0] };
  },
  yesterday: () => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return { dateFrom: d.toISOString().split('T')[0], dateTo: d.toISOString().split('T')[0] };
  },
  week: () => {
    const end = new Date();
    const start = new Date(); start.setDate(end.getDate() - 6);
    return { dateFrom: start.toISOString().split('T')[0], dateTo: end.toISOString().split('T')[0] };
  },
  month: () => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return { dateFrom: start.toISOString().split('T')[0], dateTo: end.toISOString().split('T')[0] };
  },
};

const STATUS_CHIP = {
  open:   'bg-blue-100 text-blue-700',
  closed: 'bg-green-100 text-green-700',
};

const METHOD_LABEL = {
  cash:   'Cash',
  card:   'Card',
  gcash:  'GCash',
  maya:   'Maya',
  charge: 'Charge (AR)',
};

// ─── Detail panel for a single shift ─────────────────────────────────────────

function ShiftDetail({ shiftId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reprinting, setReprinting] = useState(false);
  const [reprintMsg, setReprintMsg] = useState(null);

  useEffect(() => {
    if (!shiftId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/restaurant/pos-reports/shifts/${shiftId}`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load detail'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [shiftId]);

  async function handleReprint() {
    setReprinting(true);
    setReprintMsg(null);
    try {
      await api.post(`/restaurant/pos-reports/shifts/${shiftId}/reprint`);
      setReprintMsg({ ok: true, text: 'Reprint sent to printer.' });
    } catch (err) {
      setReprintMsg({ ok: false, text: err.response?.data?.message || 'Reprint failed.' });
    } finally {
      setReprinting(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading Z reading…</div>;
  if (error)   return <div className="p-6 text-sm text-red-600">{error}</div>;
  if (!data)   return null;

  const { shift, summary } = data;
  const isOpen = shift.status === 'open';
  const overShort = summary?.overShort ?? 0;

  return (
    <div className="bg-white border-t border-gray-200">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <div>
          <span className="font-bold text-gray-900 text-sm">
            {isOpen ? 'X READING — Shift In Progress' : 'Z READING — Closed Shift'}
          </span>
          <span className="ml-3 text-xs text-gray-500">
            {fmtDate(shift.startedAt)} · {shift.cashierName} · {shift.terminalId}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {reprintMsg && (
            <span className={`text-xs ${reprintMsg.ok ? 'text-green-600' : 'text-red-600'}`}>
              {reprintMsg.text}
            </span>
          )}
          <button
            onClick={handleReprint}
            disabled={reprinting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-50"
          >
            <Printer className="h-3.5 w-3.5" />
            {reprinting ? 'Printing…' : 'Reprint'}
          </button>
          <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600">✕ Close</button>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Column 1 — Balance */}
        <div className="space-y-4">
          <Section title="Cash Drawer">
            <Row label="Opening Float"  value={fmt(shift.openingFloat || 0)} />
            <Row label="Cash Sales"     value={fmt(summary?.cashSales || 0)} />
            <Row label="Expected Cash"  value={fmt(summary?.expectedCash || (shift.openingFloat + (summary?.cashSales || 0)))} bold />
            {shift.status === 'closed' && (
              <>
                <Row label="Declared Cash" value={fmt(shift.closingCash || 0)} bold />
                <div className={`flex justify-between text-sm font-bold pt-1 border-t border-gray-200 ${
                  overShort === 0 ? 'text-green-700' : overShort > 0 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  <span>Over / Short</span>
                  <span>{overShort === 0 ? 'BALANCED ✓' : overShort > 0 ? `OVER +${fmt(overShort)}` : `SHORT ${fmt(overShort)}`}</span>
                </div>
              </>
            )}
          </Section>

          <Section title="Sales Summary">
            <Row label="Transactions"   value={String(summary?.totalTransactions || 0)} />
            <Row label="Gross Sales"    value={fmt(summary?.grossSales || 0)} bold />
            <Row label="Discounts"      value={`-${fmt(summary?.totalDiscount || 0)}`} />
            <Row label="Net Sales"      value={fmt(summary?.netSales || 0)} bold />
            <Row label="VAT (12%)"      value={fmt(summary?.totalVAT || 0)} />
          </Section>
        </div>

        {/* Column 2 — Payment breakdown */}
        <div className="space-y-4">
          <Section title="Payment Breakdown">
            {Object.entries(summary?.byPaymentMethod || {}).map(([m, amt]) => (
              <Row key={m} label={METHOD_LABEL[m] || m.toUpperCase()} value={fmt(amt)} />
            ))}
            {!summary?.byPaymentMethod && <p className="text-xs text-gray-400">No payment data</p>}
          </Section>

          {shift.closingDenominations?.length > 0 && (
            <Section title="Closing Denominations">
              {shift.closingDenominations
                .filter(d => d.count > 0)
                .map(d => (
                  <Row
                    key={d.bill}
                    label={`₱${d.bill.toLocaleString()} × ${d.count}`}
                    value={fmt(d.bill * d.count * 100)}
                  />
                ))}
            </Section>
          )}
        </div>

        {/* Column 3 — Items sold & waiters */}
        <div className="space-y-4">
          <Section title="Top Items Sold">
            {(summary?.itemsSold || []).slice(0, 8).map(item => (
              <div key={item.name} className="flex justify-between text-xs text-gray-700 py-0.5">
                <span className="truncate pr-2">{item.qty}× {item.name}</span>
                <span className="text-gray-500 shrink-0">{fmt(item.total)}</span>
              </div>
            ))}
            {!summary?.itemsSold?.length && <p className="text-xs text-gray-400">No items</p>}
          </Section>

          <Section title="Per Waiter">
            {(summary?.byWaiter || []).map(w => (
              <Row key={w.name} label={`${w.name} (${w.orders})`} value={fmt(w.total)} />
            ))}
            {!summary?.byWaiter?.length && <p className="text-xs text-gray-400">No waiter data</p>}
          </Section>

          {(summary?.voidedItems || []).length > 0 && (
            <Section title="Voided Items">
              {summary.voidedItems.map(item => (
                <div key={item.name} className="flex justify-between text-xs text-red-600 py-0.5">
                  <span>{item.qty}× {item.name}</span>
                  <span>{fmt(item.total)}</span>
                </div>
              ))}
            </Section>
          )}

          {shift.closingNotes && (
            <Section title="Closing Notes">
              <p className="text-xs text-gray-600">{shift.closingNotes}</p>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{title}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between text-sm ${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ZReadingReportPage() {
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo,   setDateTo]   = useState(todayStr());
  const [status,   setStatus]   = useState('all');
  const [shifts,   setShifts]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);   // shiftId with expanded detail

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/restaurant/pos-reports/shifts', {
        params: { dateFrom, dateTo, status },
      });
      setShifts(res.data.shifts || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, status]);

  useEffect(() => { fetchShifts(); }, []);

  function applyPreset(key) {
    const { dateFrom: f, dateTo: t } = PRESET_DATES[key]();
    setDateFrom(f);
    setDateTo(t);
  }

  // Aggregate totals for the listed shifts
  const totals = shifts.reduce((acc, s) => {
    const sm = s.summary || {};
    acc.grossSales    += sm.grossSales    || 0;
    acc.cashSales     += sm.cashSales     || 0;
    acc.transactions  += sm.totalTransactions || 0;
    acc.shifts++;
    return acc;
  }, { grossSales: 0, cashSales: 0, transactions: 0, shifts: 0 });

  return (
    <Layout pageTitle="Z / X Reading Report">
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">Z / X Reading Report</h1>
          <button
            onClick={fetchShifts}
            disabled={loading}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-wrap gap-3 items-end">
            {/* Presets */}
            <div className="flex gap-1">
              {['today', 'yesterday', 'week', 'month'].map(p => (
                <button
                  key={p}
                  onClick={() => applyPreset(p)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 capitalize"
                >
                  {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'week' ? 'This Week' : 'This Month'}
                </button>
              ))}
            </div>

            <div className="flex gap-2 items-center">
              <input
                type="date" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
              <span className="text-gray-400 text-sm">to</span>
              <input
                type="date" value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>

            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="all">All Shifts</option>
              <option value="closed">Closed (Z Reading)</option>
              <option value="open">Open (X Reading)</option>
            </select>

            <button
              onClick={fetchShifts}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              {loading ? 'Loading…' : 'Generate'}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {/* Summary cards */}
        {shifts.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="Shifts" value={totals.shifts} />
            <Card label="Transactions" value={totals.transactions} />
            <Card label="Gross Sales" value={fmt(totals.grossSales)} green />
            <Card label="Cash Sales" value={fmt(totals.cashSales)} />
          </div>
        )}

        {/* Shift table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-8"></th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Cashier</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Terminal</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Start</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">End</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Duration</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Txns</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Gross Sales</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Cash Sales</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Over/Short</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-sm text-gray-400">Loading…</td>
                </tr>
              )}
              {!loading && shifts.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-sm text-gray-400">
                    No shifts found for the selected filters.
                  </td>
                </tr>
              )}
              {!loading && shifts.map(shift => {
                const sm = shift.summary || {};
                const over = sm.overShort ?? 0;
                const isExpanded = selected === shift._id;
                return [
                  <tr
                    key={shift._id}
                    onClick={() => setSelected(isExpanded ? null : shift._id)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-gray-400">
                      {isExpanded
                        ? <ChevronDown className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{fmtDate(shift.startedAt)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{shift.cashierName || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{shift.terminalId || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtTime(shift.startedAt)}</td>
                    <td className="px-4 py-3 text-gray-600">{shift.closedAt ? fmtTime(shift.closedAt) : <span className="text-blue-500">Active</span>}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{duration(shift.startedAt, shift.closedAt)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{sm.totalTransactions ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{sm.grossSales != null ? fmt(sm.grossSales) : '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{sm.cashSales != null ? fmt(sm.cashSales) : '—'}</td>
                    <td className={`px-4 py-3 text-right text-xs font-semibold ${
                      over === 0 ? 'text-green-700' : over > 0 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {shift.status === 'closed'
                        ? (over === 0 ? 'Balanced' : over > 0 ? `+${fmt(over)}` : fmt(over))
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CHIP[shift.status] || 'bg-gray-100 text-gray-600'}`}>
                        {shift.status === 'open' ? 'Open' : 'Closed'}
                      </span>
                    </td>
                  </tr>,
                  isExpanded && (
                    <tr key={`${shift._id}-detail`}>
                      <td colSpan={12} className="p-0">
                        <ShiftDetail shiftId={shift._id} onClose={() => setSelected(null)} />
                      </td>
                    </tr>
                  ),
                ].filter(Boolean);
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function Card({ label, value, green }) {
  return (
    <div className={`rounded-xl border p-4 ${green ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
      <p className={`text-xs font-medium mb-1 ${green ? 'text-green-700' : 'text-gray-500'}`}>{label}</p>
      <p className={`text-xl font-bold ${green ? 'text-green-900' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}
